import React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

interface PageContainerProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** Optional icon shown as a flat square badge to the left of the title. */
  icon?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  children: React.ReactNode;
  disableGutters?: boolean;
}

/**
 * Standard responsive page wrapper. Provides a centered, max-width column with
 * consistent vertical rhythm and an optional page header (title + actions)
 * that collapses to a stacked layout on small screens.
 */
const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  actions,
  icon,
  maxWidth = 'lg',
  children,
  disableGutters,
}) => {
  const hasHeader = Boolean(title || subtitle || actions);

  return (
    <Container
      maxWidth={maxWidth}
      disableGutters={disableGutters}
      sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}
    >
      {hasHeader && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            {icon && (
              <Box
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  display: { xs: 'none', sm: 'inline-flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& svg': { fontSize: 26 },
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {actions && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1 }}
            >
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      {children}
    </Container>
  );
};

export default PageContainer;
